import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconPlus, IconX, IconTrash, IconUserOff, IconUserCheck } from '@tabler/icons-react';
import api from '../services/api';
import { getCompanies, getMqttStatus } from '../services/companies';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../services/users';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  TableRow,
  StatusChip,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  FormSectionTitle,
  Label,
  Input,
  HelperText,
  ErrorAlert,
  EmptyState,
  LoadingText,
  H2Title,
  Checkbox,
  IconButton,
} from '../components/styled';

const CompanyName = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const InactiveRow = styled(TableRow)`
  opacity: 0.5;
`;

// Selection toolbar — appears above the table once one or more rows are
// checked, hosting the bulk Activate / Deactivate / Delete actions.
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.md};
`;

const BulkCount = styled.span`
  margin-right: auto;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
`;

const SelectCell = styled(Td)`
  width: 36px;
  text-align: center;
`;

const SelectTh = styled(Th)`
  width: 36px;
`;

const MrrValue = styled.span`
  font-variant-numeric: tabular-nums;
`;

const OfflineCount = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ count, theme }) =>
    count > 0 ? theme.color.red : theme.font.color.primary};
  font-weight: ${({ count, theme }) =>
    count > 0 ? theme.font.weight.semiBold : theme.font.weight.regular};
`;

const OnlineCount = styled.span`
  font-variant-numeric: tabular-nums;
`;

const HealthBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  .track {
    flex: 1;
    height: 6px;
    background: ${({ theme }) => theme.background.tertiary};
    border-radius: ${({ theme }) => theme.border.radius.pill};
    overflow: hidden;
    min-width: 60px;
  }

  .pct {
    font-size: ${({ theme }) => theme.font.size.xs};
    color: ${({ theme }) => theme.font.color.secondary};
    min-width: 32px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

type Health = 'good' | 'warn' | 'bad';

const HealthFill = styled('div', {
  shouldForwardProp: prop => prop !== 'health',
})<{ health: Health }>`
  height: 100%;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  transition: width 0.3s ease;
  background: ${({ theme, health }) =>
    health === 'good' ? theme.color.green
    : health === 'warn' ? theme.color.yellow
    : theme.color.red};
`;

const healthBucket = (pct: number): Health =>
  pct > 75 ? 'good' : pct > 50 ? 'warn' : 'bad';

// ---------------------------------------------------------------------------
// MqttStatusBadge
// ---------------------------------------------------------------------------

function MqttStatusBadge({ companyId }) {
  const { data: mqttStatus, isLoading } = useQuery({
    queryKey: ['mqtt-status', companyId],
    queryFn: () => getMqttStatus(companyId),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  if (isLoading) {
    return <StatusChip variant="neutral">...</StatusChip>;
  }

  if (mqttStatus?.status === 'online') {
    const mqttConnected = mqttStatus.health?.components?.mqtt?.connected;
    if (mqttConnected) {
      return <StatusChip variant="positive">Connected</StatusChip>;
    }
    return <StatusChip variant="warning">Degraded</StatusChip>;
  }

  if (mqttStatus?.status === 'offline') {
    return <StatusChip variant="critical">Offline</StatusChip>;
  }

  return <StatusChip variant="neutral">Unknown</StatusChip>;
}

// ---------------------------------------------------------------------------
// Companies Page
// ---------------------------------------------------------------------------

function Companies() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  if (!isSuperAdmin(user) && user?.company_id) {
    return <Navigate to={`/software-providers/${user.company_id}`} replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: getCompanies,
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/companies', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setShowModal(null);
      setFormData({});
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create company'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(`/companies/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setShowModal(null);
      setSelectedCompany(null);
      setFormData({});
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update company'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/companies/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      setPageError(null);
    },
    onError: (err) => setPageError(err.response?.data?.error || 'Failed to delete company'),
  });

  const activateMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/companies/${id}/activate`)).data,
    onSuccess: () => queryClient.invalidateQueries(['companies']),
    onError: (err) => setPageError(err.response?.data?.error || 'Failed to activate company'),
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/companies/${id}/deactivate`)).data,
    onSuccess: () => queryClient.invalidateQueries(['companies']),
    onError: (err) => setPageError(err.response?.data?.error || 'Failed to deactivate company'),
  });

  const companies = data?.companies || [];

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      price_per_device: '',
      nrf_cloud_team_id: '',
      nrf_cloud_api_key: '',
      mqtt_service_url: '',
    });
    setShowModal('create');
    setError(null);
  };

  const handleOpenEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      price_per_device: company.price_per_device || '',
      nrf_cloud_team_id: company.nrf_cloud_team_id || '',
      nrf_cloud_api_key: '',
      mqtt_service_url: company.mqtt_service_url || '',
    });
    setShowModal('edit');
    setError(null);
  };

  const handleSubmitCreate = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (data.price_per_device === '' || data.price_per_device === null) {
      data.price_per_device = 0;
    }
    createMutation.mutate(data);
  };

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    const updateData = { ...formData };
    if (!updateData.nrf_cloud_api_key) {
      delete updateData.nrf_cloud_api_key;
    }
    if (updateData.price_per_device === '' || updateData.price_per_device === null) {
      updateData.price_per_device = 0;
    }
    updateMutation.mutate({ id: selectedCompany.id, data: updateData });
  };

  const allSelected = companies.length > 0 && selectedIds.length === companies.length;

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : companies.map((c) => c.id));
  };

  const handleBulkActivate = () => {
    if (selectedIds.length === 0) return;
    setPageError(null);
    selectedIds.forEach((id) => activateMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleBulkDeactivate = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Deactivate ${selectedIds.length} software provider(s)? Their users will no longer be able to sign in.`)) return;
    setPageError(null);
    selectedIds.forEach((id) => deactivateMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} software provider(s)? This permanently deletes their users, devices and settings and cannot be undone.`)) return;
    setPageError(null);
    selectedIds.forEach((id) => deleteMutation.mutate(id));
    setSelectedIds([]);
  };

  const handleRowClick = (e, companyId) => {
    if (e.target.closest('a') || e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/software-providers/${companyId}`);
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return `$${num.toFixed(2)}`;
  };

  const computeMrr = (company) => {
    const price = parseFloat(company.price_per_device || 0);
    const deployed = parseInt(company.deployed_device_count || 0, 10);
    return price * deployed;
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Software Providers</PageTitle>
        <Button onClick={handleOpenCreate}>
          <IconPlus size={16} stroke={2} />
          Add Software Provider
        </Button>
      </PageHeader>

      {pageError && <ErrorAlert>{pageError}</ErrorAlert>}

      {selectedIds.length > 0 && (
        <BulkBar>
          <BulkCount>{selectedIds.length} selected</BulkCount>
          <Button size="sm" variant="secondary" onClick={handleBulkActivate}>
            <IconUserCheck size={14} stroke={2} />Activate
          </Button>
          <Button size="sm" variant="secondary" onClick={handleBulkDeactivate}>
            <IconUserOff size={14} stroke={2} />Deactivate
          </Button>
          <Button size="sm" variant="danger" onClick={handleBulkDelete}>
            <IconTrash size={14} stroke={2} />Delete
          </Button>
          <IconButton type="button" title="Clear selection" aria-label="Clear selection" onClick={() => setSelectedIds([])}>
            <IconX size={16} stroke={2} />
          </IconButton>
        </BulkBar>
      )}

      {isLoading ? (
        <LoadingText>Loading companies...</LoadingText>
      ) : companies.length === 0 ? (
        <EmptyState>
          <p>No companies yet.</p>
          <p>Create your first company to get started.</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <SelectTh>
                  <Checkbox checked={allSelected} onChange={toggleSelectAll} />
                </SelectTh>
                <Th>Name</Th>
                <Th>Devices</Th>
                <Th>Online</Th>
                <Th>Offline</Th>
                <Th>Deployed</Th>
                <Th>Health</Th>
                <Th>Users</Th>
                <Th>Price/Unit</Th>
                <Th>Monthly MRR</Th>
                <Th>MQTT Service</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => {
                const total = parseInt(company.device_count || 0, 10);
                const offlineCount = parseInt(company.offline_device_count || 0, 10);
                const onlineCount = Math.max(0, total - offlineCount);
                const deployedCount = parseInt(company.deployed_device_count || 0, 10);
                const healthPct = total > 0 ? Math.round((onlineCount / total) * 100) : 0;
                const Row = company.is_active === false ? InactiveRow : TableRow;
                return (
                  <Row
                    key={company.id}
                    onClick={(e) => handleRowClick(e, company.id)}
                  >
                    {/* The visible checkbox is a label/span, not the input, so the row's
                        click-to-open handler must be stopped at the cell. */}
                    <SelectCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(company.id)}
                        onChange={() => toggleSelect(company.id)}
                      />
                    </SelectCell>
                    <Td>
                      <CompanyName to={`/software-providers/${company.id}`}>
                        {company.name}
                      </CompanyName>
                    </Td>
                    <Td>{total}</Td>
                    <Td>
                      <OnlineCount>{onlineCount}</OnlineCount>
                    </Td>
                    <Td>
                      <OfflineCount count={offlineCount}>
                        {offlineCount}
                      </OfflineCount>
                    </Td>
                    <Td>{deployedCount}</Td>
                    <Td>
                      <HealthBar>
                        <div className="track">
                          <HealthFill
                            health={healthBucket(healthPct)}
                            style={{ width: `${healthPct}%` }}
                          />
                        </div>
                        <span className="pct">{healthPct}%</span>
                      </HealthBar>
                    </Td>
                    <Td>{company.user_count || 0}</Td>
                    <Td>{formatCurrency(company.price_per_device)}</Td>
                    <Td>
                      <MrrValue>{formatCurrency(computeMrr(company))}</MrrValue>
                    </Td>
                    <Td>
                      {company.mqtt_service_url ? (
                        <MqttStatusBadge companyId={company.id} />
                      ) : (
                        <StatusChip variant="neutral">Not configured</StatusChip>
                      )}
                    </Td>
                    <Td>
                      <StatusChip variant={company.is_active === false ? 'red' : 'green'}>
                        {company.is_active === false ? 'Inactive' : 'Active'}
                      </StatusChip>
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {/* Create Company Modal */}
      {showModal === 'create' && (
        <ModalOverlay onClick={() => setShowModal(null)}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Add Software Provider</H2Title>
              <ModalCloseButton onClick={() => setShowModal(null)} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmitCreate}>
              <ModalBody>
                {error && <ErrorAlert>{error}</ErrorAlert>}
                <FormGroup>
                  <Label>Company Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter company name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Price per Device ($/month)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_per_device}
                    onChange={(e) => setFormData({ ...formData, price_per_device: e.target.value })}
                    placeholder="0.00"
                  />
                </FormGroup>
                <FormSectionTitle>nRF Cloud Integration</FormSectionTitle>
                <FormGroup>
                  <Label>nRF Cloud Team ID</Label>
                  <Input
                    type="text"
                    value={formData.nrf_cloud_team_id}
                    onChange={(e) => setFormData({ ...formData, nrf_cloud_team_id: e.target.value })}
                    placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>nRF Cloud API Key</Label>
                  <Input
                    type="password"
                    value={formData.nrf_cloud_api_key}
                    onChange={(e) => setFormData({ ...formData, nrf_cloud_api_key: e.target.value })}
                    placeholder="Enter API key"
                  />
                  <HelperText>Found in nRF Cloud dashboard under Team settings</HelperText>
                </FormGroup>
                <FormSectionTitle>MQTT Service</FormSectionTitle>
                <FormGroup>
                  <Label>MQTT Service URL</Label>
                  <Input
                    type="text"
                    value={formData.mqtt_service_url}
                    onChange={(e) => setFormData({ ...formData, mqtt_service_url: e.target.value })}
                    placeholder="e.g., https://mqtt.example.com:8080"
                  />
                  <HelperText>URL of the MQTT service deployment for this company</HelperText>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={() => setShowModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Add Software Provider"}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Edit Company Modal */}
      {showModal === 'edit' && selectedCompany && (
        <ModalOverlay onClick={() => setShowModal(null)}>
          <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>Edit Company</H2Title>
              <ModalCloseButton onClick={() => setShowModal(null)} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={handleSubmitEdit}>
              <ModalBody>
                {error && <ErrorAlert>{error}</ErrorAlert>}
                <FormGroup>
                  <Label>Company Name</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter company name"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Price per Device ($/month)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_per_device}
                    onChange={(e) => setFormData({ ...formData, price_per_device: e.target.value })}
                    placeholder="0.00"
                  />
                </FormGroup>
                <FormSectionTitle>nRF Cloud Integration</FormSectionTitle>
                <FormGroup>
                  <Label>nRF Cloud Team ID</Label>
                  <Input
                    type="text"
                    value={formData.nrf_cloud_team_id}
                    onChange={(e) => setFormData({ ...formData, nrf_cloud_team_id: e.target.value })}
                    placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                  />
                  {selectedCompany?.nrf_cloud_team_id && (
                    <HelperText>Current: {selectedCompany.nrf_cloud_team_id}</HelperText>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>nRF Cloud API Key</Label>
                  <Input
                    type="password"
                    value={formData.nrf_cloud_api_key}
                    onChange={(e) => setFormData({ ...formData, nrf_cloud_api_key: e.target.value })}
                    placeholder={selectedCompany?.has_nrf_cloud_api_key ? '••••••••• (stored — leave blank to keep current)' : 'Enter API key'}
                  />
                  <HelperText>Leave blank to keep existing key</HelperText>
                </FormGroup>
                <FormSectionTitle>MQTT Service</FormSectionTitle>
                <FormGroup>
                  <Label>MQTT Service URL</Label>
                  <Input
                    type="text"
                    value={formData.mqtt_service_url}
                    onChange={(e) => setFormData({ ...formData, mqtt_service_url: e.target.value })}
                    placeholder="e.g., https://mqtt.example.com:8080"
                  />
                  <HelperText>URL of the MQTT service deployment for this company</HelperText>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={() => setShowModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default Companies;
